import React, { useState, useEffect } from 'react';
import { format } from 'rut.js';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import {
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CreditCard as PaymentsIcon,
  Download as DownloadIcon,
  CheckCircleOutline as CheckIcon,
} from '@mui/icons-material';
import axios, { AxiosError } from 'axios';
import { useYear } from '../../hooks/useYear';
import handleApiResponse from '../../utils/handleApiResponse';
import {
  getAllPaymentDocumentsPerPage,
  getPaymentDocumentUrl,
  deletePaymentDocument,
  getAllPaymentDocumentsExcel,
  validatePaymentDocument,
} from '../../requests/api/contractPayment';
import Page from '../../components/Layout/Page';
import Header from '../../components/Layout/Header';
import CustomTable from '../../components/General/CustomTable';
import EmptyTable from '../../components/General/EmptyTable';
import DeleteModal from '../../components/General/DeleteModal';
import ViewPaymentModal from './ViewPaymentModal';
import DocumentModal from '../ContractPayment/DocumentModal';
import {
  PaymentDocumentData,
  translatePaymentType,
  translatePaymentMethod,
} from './types';
import ValidatePaymentModal from './ValidatePaymentModal';
import { ReduxState } from '../../types';

const ALL_PAYMENTS_UI = [
  {
    label: 'Fecha',
    key: 'date',
    showOnTable: true,
    showOnModal: false,
    typeField: 'shortDate',
    valueType: 'shortDate',
  },
  {
    label: 'Monto',
    key: 'amount',
    showOnTable: true,
    showOnModal: false,
    typeField: 'text',
    valueType: 'ccy',
  },
  {
    label: 'Nombre Alumno',
    key: 'studentFullName',
    showOnTable: true,
    showOnModal: false,
    typeField: 'text',
    valueType: 'string',
  },
  {
    label: 'Tipo',
    key: 'type',
    showOnTable: true,
    showOnModal: false,
    typeField: 'text',
    valueType: 'string',
  },
  {
    label: 'Medio de pago',
    key: 'paymentMethod',
    showOnTable: true,
    showOnModal: false,
    typeField: 'text',
    valueType: 'string',
  },
  {
    label: 'Estado del pago',
    key: 'validated',
    showOnTable: true,
    showOnModal: false,
    typeField: 'text',
    valueType: 'custom',
    functionToApply: (value: boolean) => [
      <Typography
        key="validated-status"
        sx={{
          fontSize: 'clamp(12px, 0.83333vw, 16px) !important',
          color: value ? 'inherit' : '#E94745',
        }}
      >
        {value ? 'Validado' : 'Validación pendiente'}
      </Typography>,
    ] as JSX.Element[],
  },
  {
    label: 'Ver comprobante',
    key: 'viewReceipt',
    showOnTable: true,
    showOnModal: false,
    typeField: 'button',
    valueType: 'string',
  },
  {
    label: 'Validar pago',
    key: 'validatePayment',
    showOnTable: true,
    showOnModal: false,
    typeField: 'button',
    valueType: 'string',
  },
  {
    label: 'Acciones',
    key: 'actions',
    showOnTable: true,
    showOnModal: false,
    typeField: 'button',
    valueType: 'string',
  },
];

function AllPaymentsView() {
  const { enqueueSnackbar } = useSnackbar();
  const { selectedYear } = useYear();
  const user = useSelector((state: ReduxState) => state.account.user);
  const [allPayments, setAllPayments] = useState<{
    items: PaymentDocumentData[],
    totalItems: number,
  }>({
    items: [],
    totalItems: 0,
  });
  const [tableParameters, setTableParameters] = useState<{ page: number, rowsPerPage: number }>({
    page: 0,
    rowsPerPage: 10,
  });
  const [searchValue, setSearchValue] = useState<string>('');
  const [openViewModal, setOpenViewModal] = useState<boolean>(false);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<{ id: string; open: boolean }>({
    id: '',
    open: false,
  });
  const [openValidatePaymentModal, setOpenValidatePaymentModal] = useState<boolean>(false);
  const [selectedValidatePayment, setSelectedValidatePayment] = useState<{
    studentId: string;
    paymentDocumentId: string;
    amount: number;
    imageUrl: string;
  } | null>(null);
  const [updateTable, setUpdateTable] = useState<boolean>(false);
  const [selectedPayment, setSelectedPayment] = useState<{
    date: string;
    amount: number;
    studentName: string;
    studentLastname: string;
    studentRut: string;
    type: 'tuition' | 'fee';
    paymentMethod: string;
    comment: string;
    imageUrl: string;
  } | null>(null);
  const [editPaymentData, setEditPaymentData] = useState<{
    paymentDocumentId: string;
    amount: number;
    comment: string;
    paymentMethod: string;
  } | null>(null);
  const [showDownloadExcelLoading, setShowDownloadExcelLoading] = useState<boolean>(false);

  useEffect(() => {
    const source = axios.CancelToken.source();
    const getAllPayments = async () => {
      try {
        const { paymentDocuments: { rows, count } } = await getAllPaymentDocumentsPerPage({
          page: tableParameters.page,
          limit: tableParameters.rowsPerPage,
          searchValue,
          year: selectedYear,
        }, source.token);

        const paymentsWithActions = rows.map((payment: PaymentDocumentData) => ({
          ...payment,
          originalType: payment.type,
          originalPaymentMethod: payment.paymentMethod,
          studentFullName: `${payment.studentName} ${payment.studentLastname}`,
          studentRut: format(payment.studentRut),
          type: translatePaymentType(payment.type),
          paymentMethod: translatePaymentMethod(payment.paymentMethod || '-'),
          viewReceipt: (
            <IconButton
              onClick={() => handleViewReceipt({
                ...payment,
                type: payment.type,
                paymentMethod: payment.paymentMethod,
              })}
              sx={{ padding: 0, margin: 0 }}
            >
              <VisibilityIcon color="primary" />
            </IconButton>
          ),
          validatePayment: !payment.validated && user?.email === 'contabilidad@carezycordova.cl' ? (
            <IconButton
              onClick={() => handleValidatePayment(payment)}
              sx={{ padding: 0, margin: 0 }}
            >
              <CheckIcon color="primary" />
            </IconButton>
          ) : null,
          actions: (
            <Box display="flex" gap={1} justifyContent="center">
              <IconButton
                onClick={() => handleEdit(payment)}
                sx={{ padding: 0, margin: 0 }}
              >
                <EditIcon color="primary" />
              </IconButton>
              <IconButton
                onClick={() => handleDelete(payment.paymentDocumentId.toString())}
                sx={{ padding: 0, margin: 0 }}
              >
                <DeleteIcon color="error" />
              </IconButton>
            </Box>
          ),
        }));

        setAllPayments({ items: paymentsWithActions, totalItems: count });
      } catch (err) {
        const e = err as AxiosError;
        handleApiResponse(enqueueSnackbar, e, false);
      }
    };

    getAllPayments();

    return () => {
      source.cancel();
    };
  }, [
    tableParameters.page,
    tableParameters.rowsPerPage,
    searchValue,
    updateTable,
    selectedYear,
  ]);

  const handleViewReceipt = async (payment: PaymentDocumentData) => {
    try {
      const { url } = await getPaymentDocumentUrl(payment.contractPaymentId.toString());
      setSelectedPayment({
        date: payment.date,
        amount: payment.amount,
        studentName: payment.studentName,
        studentLastname: payment.studentLastname,
        studentRut: payment.studentRut,
        type: payment.type,
        paymentMethod: payment.paymentMethod,
        comment: payment.comment,
        imageUrl: url,
      });
      setOpenViewModal(true);
    } catch (err) {
      const e = err as AxiosError;
      handleApiResponse(enqueueSnackbar, e, false);
    }
  };

  const handleValidatePayment = async (payment: PaymentDocumentData) => {
    try {
      const { url } = await getPaymentDocumentUrl(payment.contractPaymentId.toString());
      setSelectedValidatePayment({
        studentId: payment.studentId,
        paymentDocumentId: payment.paymentDocumentId.toString(),
        amount: payment.amount,
        imageUrl: url,
      });
      setOpenValidatePaymentModal(true);
    } catch (err) {
      const e = err as AxiosError;
      handleApiResponse(enqueueSnackbar, e, false);
    }
  };

  const handleValidatePaymentConfirm = async (paymentDocumentId: string) => {
    try {
      if (!selectedValidatePayment) {
        throw new Error('No se ha seleccionado un pago');
      }

      if (!selectedValidatePayment?.studentId) {
        throw new Error('El alumno no se ha cargado correctamente');
      }

      const response = await validatePaymentDocument(paymentDocumentId, selectedValidatePayment?.studentId ?? '');
      handleApiResponse(enqueueSnackbar, response, true);
      setUpdateTable(!updateTable);
      setOpenValidatePaymentModal(false);
    } catch (err) {
      const e = err as AxiosError;
      handleApiResponse(enqueueSnackbar, e, false);
      throw e;
    }
  };

  const handleEdit = (payment: PaymentDocumentData) => {
    setEditPaymentData({
      paymentDocumentId: payment.paymentDocumentId.toString(),
      amount: payment.amount,
      comment: payment.comment,
      paymentMethod: payment.originalPaymentMethod ?? payment.paymentMethod ?? '',
    });
    setOpenEditModal(true);
  };

  const handleEditSuccess = () => {
    setUpdateTable(!updateTable);
  };

  const handleDelete = (paymentDocumentId: string) => {
    setOpenDeleteModal({ id: paymentDocumentId, open: true });
  };

  const handleDeleteFunction = async (paymentDocumentId: string) => {
    try {
      const response = await deletePaymentDocument(paymentDocumentId);
      handleApiResponse(enqueueSnackbar, response, true);
      handleDeleteCloseModal(setOpenDeleteModal);
    } catch (err) {
      const e = err as AxiosError;
      handleApiResponse(enqueueSnackbar, e, false);
    }
  };

  const handleDeleteCloseModal = (setFunction: React.Dispatch<React.SetStateAction<{
    id: string;
    open: boolean;
  }>>) => {
    setFunction(() => ({ id: '', open: false }));
    setUpdateTable(!updateTable);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleDownloadExcel = async () => {
    try {
      setShowDownloadExcelLoading(true);
      const blob: Blob = await getAllPaymentDocumentsExcel(searchValue, selectedYear);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'pagos.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setShowDownloadExcelLoading(false);
    } catch (err) {
      const e = err as AxiosError;
      handleApiResponse(enqueueSnackbar, e, false);
      setShowDownloadExcelLoading(false);
    }
  };

  return (
    <Page title="Pagos">
      <Header
        title="Pagos"
        icon={<PaymentsIcon fontSize="large" />}
        buttons={[
          {
            text: 'Descargar Excel',
            icon: <DownloadIcon />,
            onClick: handleDownloadExcel,
            disabled: allPayments.totalItems === 0,
            loading: showDownloadExcelLoading,
          },
        ]}
        search={{
          text: 'Buscar alumno...',
          value: searchValue,
          onChangeSearchValue: handleSearchChange,
        }}
      />
      <Box mt={3}>
        {allPayments.totalItems !== 0 && (
          <CustomTable
            headers={ALL_PAYMENTS_UI.filter((elem) => elem.showOnTable)}
            data={{ values: allPayments.items, count: allPayments.totalItems }}
            tableParameters={tableParameters}
            setTableParameters={setTableParameters}
          />
        )}
        {allPayments.totalItems === 0 && <EmptyTable />}
      </Box>

      <ViewPaymentModal
        open={openViewModal}
        onClose={() => setOpenViewModal(false)}
        paymentData={selectedPayment}
      />

      <DocumentModal
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        onSuccess={handleEditSuccess}
        paymentDocumentId={editPaymentData?.paymentDocumentId}
        initialAmount={editPaymentData?.amount}
        initialComment={editPaymentData?.comment}
        initialPaymentMethod={editPaymentData?.paymentMethod}
        mode="edit"
      />

      <DeleteModal
        title="pago"
        modalDelete={openDeleteModal}
        setModalDelete={setOpenDeleteModal}
        handleCloseModal={handleDeleteCloseModal}
        deleteFunction={handleDeleteFunction}
      />

      <ValidatePaymentModal
        open={openValidatePaymentModal}
        onClose={() => setOpenValidatePaymentModal(false)}
        onValidate={handleValidatePaymentConfirm}
        paymentData={selectedValidatePayment}
      />
    </Page>
  );
}

export default AllPaymentsView;
